import { Button, DrawerCard, DrawerField, DrawerTitle, Input } from "@mixitone/components";
import PlusIcon from "@mixitone/icons/plus.svg";
import RacketIcon from "@mixitone/icons/racket.svg";
import { TournamentCompetition, TournamentEntry, TournamentPlayer } from "@mixitone/models";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import { List, setupModelRelations } from "@mixitone/oom";
import { isPresent } from "@mixitone/util";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import { injectModal } from "lib/injectParams";
import { TournamentMatchesController } from "components/Pages/Tournament/TournamentMatches/TournamentMatchesController";
import { TournamentEntrySearch } from "components/Forms/TournamentEntrySearch";
import routeService from "services/RouteService";
import { messagesService } from "services";

interface Props {
  id: string;
  competitionId: string;
}

interface State {
  round: number;
  entry1: TournamentEntry | null;
  entry2: TournamentEntry | null;
  allEntries: List<TournamentEntry>;
  saving: boolean;
}

class CreateMatchController extends ApplicationController<State, Props> {
  get initialState(): State {
    return {
      round: 0,
      entry1: null,
      entry2: null,
      allEntries: new List(),
      saving: false,
    };
  }

  async initialize(props: Props) {
    await this.changeProps(props);
  }

  async changeProps(props: Props) {
    const competition = await TournamentCompetition.query().find(props.competitionId);
    if (!competition) return;

    const entries = await TournamentEntry.query()
      .eq("tournament_competition_id", props.competitionId)
      .preload("registrations", "competition")
      .all();
    const registrations = entries.flatMap((e) => e.registrations);
    const players = await TournamentPlayer.query()
      .in("id", registrations.map((r) => r.tournament_player_id).filter(isPresent))
      .all();
    setupModelRelations({ entries, registrations, players });
    this.state.allEntries = new List(...entries);
  }

  actionSetRound(round: number) {
    this.state.round = round;
  }

  actionSetEntry1(entry: TournamentEntry) {
    this.state.entry1 = entry;
  }

  actionSetEntry2(entry: TournamentEntry) {
    this.state.entry2 = entry;
  }

  async actionCreateMatch() {
    if (this.state.saving) return;
    if (!this.state.entry1) {
      messagesService.addMessage("Please select at least one entry", "error");
      return;
    }

    this.state.saving = true;
    try {
      const controller = this.get(TournamentMatchesController);
      await controller.actionCreateMatch(this.state.round, this.state.entry1.id!, this.state.entry2?.id);
      routeService.closeModal();
      messagesService.addMessage("Match created", "success");
    } catch (error) {
      messagesService.addMessage(`Error creating match: ${error}`, "error");
    } finally {
      this.state.saving = false;
    }
  }
}

const CreateMatchDrawer = ApplicationView<Props>(({ competitionId: _competitionId }: Props) => {
  const controller = CreateMatchController.use();
  const { round, entry1, entry2, allEntries, saving } = controller.state;

  return (
    <RoutedDrawer>
      <DrawerTitle icon={<RacketIcon className="mr-2 w-[24px] fill-gray-400" />}>Create Match</DrawerTitle>
      <div className="space-y-6">
        <DrawerCard>
          <DrawerField label="Round">
            <Input
              type="number"
              min="0"
              value={round}
              onChange={(e) => controller.actionSetRound(parseInt(e.target.value, 10) || 0)}
              disabled={saving}
            />
          </DrawerField>
          <DrawerField label="Entry 1 (required)">
            <TournamentEntrySearch
              entries={allEntries}
              value={entry1}
              onSelect={(entry) => controller.actionSetEntry1(entry)}
              placeholder="Select first entry"
              disabled={saving}
            />
          </DrawerField>
          <DrawerField label="Entry 2 (optional)">
            <TournamentEntrySearch
              entries={allEntries}
              value={entry2}
              onSelect={(entry) => controller.actionSetEntry2(entry)}
              placeholder="Select second entry"
              disabled={saving}
            />
          </DrawerField>
        </DrawerCard>
        <DrawerCard>
          <div className="flex justify-end gap-2">
            <Button kind="secondary" onClick={() => routeService.closeModal()} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={() => controller.actionCreateMatch()}
              disabled={saving || !entry1}
              spinner={saving}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Create Match
            </Button>
          </div>
        </DrawerCard>
      </div>
    </RoutedDrawer>
  );
});

export default injectModal(CreateMatchController.scope(CreateMatchDrawer));
